(() => {
  const PREORDER_TAGS = new Set(['preorder-only', 'pre-order']);
  const CIPUTAT_TAG = 'ciputat-only';
  const MESSAGE_PREORDER =
    'Tidak bisa checkout product pre-order dengan product ready stock';
  const MESSAGE_DEFECT =
    'Tidak bisa checkout product defect dengan product normal';

  const tagCache = new Map();

  const normalizeTags = (product) => {
    if (!Array.isArray(product?.tags)) return [];
    return product.tags.map((tag) => String(tag).toLowerCase());
  };

  const productIsPreorder = (product) => {
    const tags = normalizeTags(product);
    if (tags.some((tag) => PREORDER_TAGS.has(tag))) return true;
    return String(product?.template_suffix || '') === 'pre-order';
  };

  const productIsCiputat = (product) =>
    normalizeTags(product).includes(CIPUTAT_TAG);

  const productIsDefect = (product) =>
    String(product?.title || '')
      .toLowerCase()
      .includes('defect');

  const getCart = () =>
    fetch(`${(window.routes && window.routes.cartUrl) || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then((response) => {
      if (!response.ok) throw response;
      return response.json();
    });

  const getHandleFromItem = (item) => {
    if (item.handle) return item.handle;
    if (!item.url) return '';
    const match = String(item.url).match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const fetchProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (tagCache.has(handle)) return Promise.resolve(tagCache.get(handle));

    return fetch(`/products/${handle}.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw response;
        return response.json();
      })
      .then((product) => {
        tagCache.set(handle, product);
        return product;
      })
      .catch(() => {
        tagCache.set(handle, null);
        return null;
      });
  };

  // Buy x Get y free-gift lines count like any other line: a ready-stock gift can
  // NOT be checked out together with pre-order products.
  const getCartMixState = (cart) => {
    const items = cart?.items || [];
    const totalQty = cart?.item_count || 0;
    if (!items.length) {
      return Promise.resolve({
        totalQty: 0,
        preorderQty: 0,
        ciputatQty: 0,
        defectQty: 0,
        preorderMixed: false,
        ciputatMixed: false,
        defectCiputatBlocked: false,
        checkoutBlocked: false,
      });
    }

    return Promise.all(items.map((item) => fetchProduct(getHandleFromItem(item)))).then(
      (products) => {
        let preorderQty = 0;
        let ciputatQty = 0;
        let defectQty = 0;

        items.forEach((item, index) => {
          const product = products[index];
          const qty = item.quantity || 0;
          if (productIsPreorder(product)) preorderQty += qty;
          if (productIsCiputat(product)) ciputatQty += qty;
          if (productIsDefect(product)) defectQty += qty;
        });

        const preorderMixed = preorderQty > 0 && preorderQty !== totalQty;
        const ciputatMixed = ciputatQty > 0 && ciputatQty !== totalQty;
        const defectMixed = defectQty > 0 && defectQty !== totalQty;
        const defectCiputatBlocked = defectMixed && ciputatMixed;
        const checkoutBlocked = preorderMixed || ciputatMixed || defectCiputatBlocked;

        return {
          totalQty,
          preorderQty,
          ciputatQty,
          defectQty,
          preorderMixed,
          ciputatMixed,
          defectCiputatBlocked,
          checkoutBlocked,
        };
      }
    );
  };

  const messageForState = (state) => {
    if (state.defectCiputatBlocked) return MESSAGE_DEFECT;
    if (state.preorderMixed || state.ciputatMixed) return MESSAGE_PREORDER;
    return '';
  };

  const assertCanAddProduct = (product) =>
    getCart().then(getCartMixState).then((state) => {
      const addingPreorder = productIsPreorder(product);
      const addingCiputat = productIsCiputat(product);
      const addingDefect = productIsDefect(product);

      if (state.totalQty === 0) return;

      const hasPreorder = state.preorderQty > 0;
      const hasRegular = state.preorderQty < state.totalQty;
      const hasCiputat = state.ciputatQty > 0;
      const hasNonCiputat = state.ciputatQty < state.totalQty;

      if (addingPreorder && hasRegular) {
        const error = new Error(MESSAGE_PREORDER);
        error.code = 'PREORDER_CART_MIX';
        throw error;
      }
      if (!addingPreorder && hasPreorder) {
        const error = new Error(MESSAGE_PREORDER);
        error.code = 'PREORDER_CART_MIX';
        throw error;
      }
      if (addingCiputat && hasNonCiputat) {
        const error = new Error(MESSAGE_PREORDER);
        error.code = 'PREORDER_CART_MIX';
        throw error;
      }
      if (!addingCiputat && hasCiputat) {
        const error = new Error(MESSAGE_PREORDER);
        error.code = 'PREORDER_CART_MIX';
        throw error;
      }

      if (addingDefect || state.defectQty > 0) {
        /* Live theme allows defect + normal unless ciputat is also mixed */
      }
    });

  const showError = (message, anchor) => {
    const text = message || MESSAGE_PREORDER;
    if (anchor && anchor.insertAdjacentElement) {
      const root = anchor.closest('.cart-drawer__footer, .cart-page__footer, form') || anchor.parentNode;
      const existing = root && root.querySelector('.CiPreorderCart__Error');
      if (existing) existing.remove();

      const errorMessageElement = document.createElement('p');
      errorMessageElement.className = 'CiPreorderCart__Error text--subdued';
      errorMessageElement.setAttribute('role', 'alert');
      errorMessageElement.textContent = text;
      anchor.insertAdjacentElement('beforebegin', errorMessageElement);
      setTimeout(() => errorMessageElement.remove(), 10000);
      return;
    }

    window.alert(text);
  };

  const getProductHandleFromForm = (form) => {
    const handleInput = form.querySelector('[name="handle"], [data-product-handle]');
    if (handleInput) {
      return handleInput.value || handleInput.getAttribute('data-product-handle') || '';
    }
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const initAddToCartGuard = () => {
    if (document.documentElement.dataset.ciPreorderCartGuard === 'true') return;
    document.documentElement.dataset.ciPreorderCartGuard = 'true';

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form || !form.matches) return;
        if (!form.matches('[data-ci-product-form], form[action*="/cart/add"]')) return;

        if (form.dataset.ciPreorderChecked === '1') {
          delete form.dataset.ciPreorderChecked;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitBtn = form.querySelector('[data-ci-add-to-cart], [data-action="add-to-cart"], [type="submit"]');
        if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');

        const handle = getProductHandleFromForm(form);
        fetchProduct(handle)
          .then((product) => assertCanAddProduct(product))
          .then(() => {
            form.dataset.ciPreorderChecked = '1';
            if (submitBtn) submitBtn.removeAttribute('disabled');
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitBtn || undefined);
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          })
          .catch((error) => {
            if (submitBtn) submitBtn.removeAttribute('disabled');
            showError(error && error.message, submitBtn || form);
          });
      },
      true
    );
  };

  const initCheckoutGuard = () => {
    if (document.documentElement.dataset.ciPreorderCheckoutGuard === 'true') return;
    document.documentElement.dataset.ciPreorderCheckoutGuard = 'true';

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form || !form.matches) return;
        if (!form.matches('[data-cart-page] form, .submit-form-checkout')) return;

        if (form.dataset.ciCheckoutChecked === '1') {
          delete form.dataset.ciCheckoutChecked;
          return;
        }

        const checkoutBtn = form.querySelector('[name="checkout"]');
        if (!checkoutBtn) return;

        if (event.submitter && event.submitter !== checkoutBtn && !checkoutBtn.contains(event.submitter)) {
          return;
        }

        if (checkoutBtn.disabled || checkoutBtn.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }

        event.preventDefault();

        const proceedToCheckout = () => {
          form.dataset.ciCheckoutChecked = '1';
          if (checkoutBtn) {
            checkoutBtn.removeAttribute('disabled');
            checkoutBtn.removeAttribute('aria-disabled');
          }

          let hiddenCheckout = form.querySelector('input[type="hidden"][name="checkout"]');
          if (!hiddenCheckout) {
            hiddenCheckout = document.createElement('input');
            hiddenCheckout.type = 'hidden';
            hiddenCheckout.name = 'checkout';
            hiddenCheckout.value = 'checkout';
            form.appendChild(hiddenCheckout);
          }

          if (typeof form.requestSubmit === 'function') {
            try {
              form.requestSubmit(checkoutBtn);
              return;
            } catch (err) {
              // fallback below
            }
          }

          form.submit();
        };

        getCart()
          .then(getCartMixState)
          .then((state) => {
            if (state.checkoutBlocked) {
              if (checkoutBtn) {
                checkoutBtn.removeAttribute('disabled');
                checkoutBtn.removeAttribute('aria-disabled');
              }
              showError(messageForState(state), checkoutBtn);
              return;
            }
            proceedToCheckout();
          })
          .catch(() => {
            proceedToCheckout();
          });
      },
      false
    );
  };

  window.CiPreorderCart = {
    PREORDER_TAGS,
    productIsPreorder,
    productIsCiputat,
    getCartMixState,
    assertCanAddProduct,
    showError,
    messageForState,
  };

  const boot = () => {
    initAddToCartGuard();
    initCheckoutGuard();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
